import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import ViewHeader from '../components/ViewHeader';
import KpiCard from '../components/KpiCard';
import CategoryBar from '../components/CategoryBar';

const kpis = [
    { title: 'Appointments scheduled', value: '42', change: '12%', changeType: 'up' },
    { title: 'Vet utilization', value: '85%', change: '9%', changeType: 'up' },
    { title: 'Bed occupancy', value: '73%', change: '9%', changeType: 'down' },
];

const appointmentBars = [
    { label: 'Saturday', value: 90 },
    { label: 'Monday', value: 65 },
];

const capacityBars = [
    { label: 'Veterinarians', value: 80 },
    { label: 'Admin', value: 65 },
];

const dailyAppointments = [
    { name: 'Sun', value: 8 },
    { name: 'Mon', value: 4 },
    { name: 'Tue', value: 6 },
    { name: 'Wed', value: 12 },
    { name: 'Thu', value: 7 },
    { name: 'Fri', value: 5 },
    { name: 'Sat', value: 15 },
];

const stayData = [
    { name: '1 day', value: 15 },
    { name: '2 to 5 days', value: 35 },
    { name: '6 to 10 days', value: 40 },
    { name: '10+ days', value: 10 },
];

const STAY_COLORS = ['#0f766e', '#f97316', '#10b981', '#3b82f6'];

const CapacityUtilization = ({ onBack }) => {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <ViewHeader title="Clinic Capacity and Utilization" subtitle="April 2026" onBack={onBack} />

            {/* Row 1: Charts on top */}
            <div className="clinic-dashboard-section-row">
                {/* Appointments by day */}
                <div className="clinic-dashboard-section-card" style={{ animationDelay: '0ms' }}>
                    <div className="clinic-dashboard-section-title">Appointments by day</div>
                    <div className="clinic-dashboard-chart-wrap" style={{ minHeight: '220px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={dailyAppointments} barSize={24} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                    dy={10}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                />
                                <Tooltip
                                    cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                />
                                <Bar animationBegin={600} dataKey="value" fill="#0f766e" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Length of stay distribution */}
                <div className="clinic-dashboard-section-card" style={{ animationDelay: '80ms' }}>
                    <div className="clinic-dashboard-section-title">Length of stay distribution</div>
                    <div className="clinic-dashboard-chart-wrap" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '260px' }}>
                        <ResponsiveContainer width="100%" height="85%">
                            <PieChart>
                                <Pie
                                    data={stayData}
                                    innerRadius={60}
                                    outerRadius={90}
                                    paddingAngle={3}
                                    dataKey="value"
                                    stroke="none"
                                >
                                    {stayData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={STAY_COLORS[index % STAY_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="clinic-dashboard-legend">
                            {stayData.map((entry, index) => (
                                <div key={index} className="clinic-dashboard-legend-item">
                                    <span className="clinic-dashboard-legend-dot" style={{ backgroundColor: STAY_COLORS[index] }} />
                                    <span className="clinic-dashboard-legend-label">{entry.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Row 2: Appointments booked | Capacity utilization breakdown */}
            <div className="clinic-dashboard-section-row">
                {/* Appointments booked */}
                <div className="clinic-dashboard-section-card" style={{ alignSelf: 'start', animationDelay: '160ms' }}>
                    <div className="clinic-dashboard-section-title">Appointments booked</div>
                    <div className="clinic-dashboard-catbar-list">
                        {appointmentBars.map((bar, i) => <CategoryBar key={i} {...bar} />)}
                    </div>
                </div>

                {/* Capacity utilization breakdown */}
                <div className="clinic-dashboard-section-card" style={{ alignSelf: 'start', animationDelay: '240ms' }}>
                    <div className="clinic-dashboard-section-title">Capacity utilization breakdown</div>
                    <div className="clinic-dashboard-catbar-list">
                        {capacityBars.map((bar, i) => <CategoryBar key={i} {...bar} />)}
                    </div>
                </div>
            </div>

            {/* Row 3: Resource availability | Current Utilization */}
            <div className="clinic-dashboard-section-row">
                {/* Resource availability */}
                <div className="clinic-dashboard-section-card" style={{ animationDelay: '320ms' }}>
                    <div className="clinic-dashboard-section-title">Resource availability</div>
                    <div className="clinic-dashboard-resource-grid">
                        <div className="clinic-dashboard-resource-item">
                            <div className="clinic-dashboard-resource-value">5</div>
                            <div className="clinic-dashboard-resource-label">Rooms available</div>
                        </div>
                        <div className="clinic-dashboard-resource-item">
                            <div className="clinic-dashboard-resource-value">4</div>
                            <div className="clinic-dashboard-resource-label">Vets on duty</div>
                        </div>
                        <div className="clinic-dashboard-resource-item">
                            <div className="clinic-dashboard-resource-value">12</div>
                            <div className="clinic-dashboard-resource-label">Kennels free</div>
                        </div>
                        <div className="clinic-dashboard-resource-item">
                            <div className="clinic-dashboard-resource-value">2</div>
                            <div className="clinic-dashboard-resource-label">ICU beds free</div>
                        </div>
                    </div>
                </div>

                {/* Current Utilization */}
                <div className="clinic-dashboard-section-card" style={{ animationDelay: '400ms' }}>
                    <div className="clinic-dashboard-section-title">Current Utilization</div>
                    <div className="clinic-dashboard-kpi-row">
                        {kpis.map((kpi, i) => <KpiCard key={i} {...kpi} />)}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CapacityUtilization;
